import { inject, Injectable, OnDestroy } from '@angular/core';
import { isDefined } from '@sama/js-core';
import { clamp, forEach, range } from 'lodash';
import { combineLatest } from 'rxjs';
import { filter } from 'rxjs/operators';
import { EnhancedCloudSettingsState } from '../../../../cross-team-shared/state/enhanced-point-cloud-setting-state';
import { PointCloudAssetService } from '../../../utils/loading/point-cloud-asset.service';
import { AbstractMeshService } from '../abstract-mesh.service';
import { PointCloudHelper } from './point-cloud-helper';

@Injectable()
export class PointCloudService
  extends AbstractMeshService<PointCloudHelper>
  implements OnDestroy
{
  private assetService = inject(PointCloudAssetService);
  private clouds = new Map<number, PointCloudHelper>();

  constructor() {
    super();
    this.subscribeToFrameLoaded();
    this.subscribeToFrameChanges();
  }

  getCurrentCloud() {
    return this.getHelper(this.store.state().render.frameNumber);
  }

  override ngOnDestroy() {
    this.clouds.forEach((cloud) => cloud.dispose());
    super.ngOnDestroy();
  }

  private subscribeToFrameLoaded() {
    this.subscribeTo(
      this.store('sensorRendererState')('sensorFrames').$,
      (sensorFrames) => {
        forEach(sensorFrames, (_, frameNumber) => {
          const attributes = this.assetService.getAsset(frameNumber);
          if (this.clouds.has(frameNumber) || !attributes) {
            return;
          }

          // build helpers ahead of time and only once, so that we can compute
          // their bounding spheres ahead of time, otherwise three.js will
          // re-compute it at render time
          const helper = new PointCloudHelper(frameNumber, attributes);
          helper.cloud.geometry.computeBoundingSphere();

          this.clouds.set(frameNumber, helper);
        });
      },
    );
  }

  private subscribeToFrameChanges() {
    this.subscribeTo(
      combineLatest([
        this.renderStore('frameNumber').$,
        this.renderStore('enhancedCloudSettings').$,
        this.store('sensorRendererState')('sensorFrames').$.pipe(
          filter(isDefined),
        ),
      ]),
      ([frameNumber, enhancedCloudSettings]) =>
        this.updateVisibleClouds(frameNumber, enhancedCloudSettings),
    );
  }

  private updateVisibleClouds(
    currentFrameNumber: number,
    enhancedCloudSettings: EnhancedCloudSettingsState,
  ) {
    let visibleFrameRange = new Set([currentFrameNumber]);

    if (enhancedCloudSettings.enabled) {
      const frameRange = this.getEnhancedFrameRange();
      visibleFrameRange = new Set(range(frameRange[0], frameRange[1] + 1));
    }

    const maxFrameNumber = this.store.getMaxFrameNumber();
    for (let frameNumber = 0; frameNumber < maxFrameNumber; frameNumber++) {
      if (visibleFrameRange.has(frameNumber) && this.clouds.has(frameNumber)) {
        this.addHelper(frameNumber, this.clouds.get(frameNumber)!);
      } else {
        this.removeHelper(frameNumber);
      }
    }
  }

  private getEnhancedFrameRange() {
    const { enhancedCloudSettings, frameNumber } = this.store.state().render;
    const frameCount = enhancedCloudSettings.frames;

    let lowerBound: number;
    let upperBound: number;
    switch (enhancedCloudSettings.direction) {
      case 'past':
        lowerBound = frameNumber - frameCount;
        upperBound = frameNumber;
        return this.clampFrameRange(lowerBound, upperBound);
      case 'all':
        lowerBound = frameNumber - Math.ceil(frameCount / 2);
        upperBound = frameNumber + Math.ceil(frameCount / 2);
        return this.clampMobileFrameRange(lowerBound, upperBound);
      case 'future':
        lowerBound = frameNumber;
        upperBound = frameNumber + frameCount;
        return this.clampFrameRange(lowerBound, upperBound);
    }
  }

  private clampFrameRange(lowerBound: number, upperBound: number) {
    const maxFrameNumber =
      this.store.state().sensorRendererState.sensorFrames.length - 1;
    return [lowerBound, upperBound].map((value) =>
      clamp(value, 0, maxFrameNumber),
    );
  }

  // if the range is out of bounds, moves the range left or right to preserve
  // range length
  private clampMobileFrameRange(lowerBound: number, upperBound: number) {
    const maxFrameNumber =
      this.store.state().sensorRendererState.sensorFrames.length - 1;

    if (lowerBound < 0) {
      return this.clampFrameRange(0, upperBound + Math.abs(lowerBound));
    }
    if (upperBound > maxFrameNumber) {
      const numberOfMissedFrames = upperBound - maxFrameNumber;
      return this.clampFrameRange(
        lowerBound - numberOfMissedFrames,
        maxFrameNumber,
      );
    }

    return [lowerBound, upperBound];
  }
}
